import { getItemContextKey } from "./itemContextKey";

export const CHAT_SESSION_STORE_KEY = "outlookChatSessions";
export const DRAFT_CONTEXT_KEY = "draftContext";

const readStore = () => {
  try {
    const raw = localStorage.getItem(CHAT_SESSION_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (map) => {
  try {
    localStorage.setItem(CHAT_SESSION_STORE_KEY, JSON.stringify(map || {}));
  } catch {
    // ignore quota / private mode errors
  }
};

/**
 * @param {Array<object>} messages
 * @returns {Array<object>}
 */
export const serializeMessagesForCache = (messages) => {
  if (!Array.isArray(messages)) {
    return [];
  }
  return messages.map((message) => ({
    id: message.id,
    messageId: message.messageId,
    text: message.text,
    sender: message.sender,
    timestamp: message.timestamp instanceof Date ? message.timestamp.toISOString() : message.timestamp,
    links: message.links,
    positiveFeedback: message.positiveFeedback,
    negativeFeedback: message.negativeFeedback,
    feedbackMessage: message.feedbackMessage,
  }));
};

/**
 * Message-specific keys for read-mode explorer sessions.
 * Do not include conversationId — thread siblings would collide.
 * @param {object | null | undefined} snapshot
 * @returns {string[]}
 */
export const getReadSessionCacheKeys = (snapshot) => {
  if (snapshot?.internetMessageId) {
    return [snapshot.internetMessageId];
  }
  return getItemContextKey(snapshot) ? [getItemContextKey(snapshot)] : [];
};

/**
 * Keys for compose / cross-pane session sharing (reply propagation).
 * @param {object | null | undefined} snapshot
 * @returns {string[]}
 */
export const getComposeSessionCacheKeys = (snapshot) => {
  const keys = new Set();
  if (snapshot?.entryId) {
    keys.add(snapshot.entryId);
  }
  if (snapshot?.inReplyTo) {
    keys.add(snapshot.inReplyTo);
  }
  if (snapshot?.internetMessageId) {
    keys.add(snapshot.internetMessageId);
  }
  return [...keys];
};

/**
 * @param {object | null | undefined} snapshot
 * @returns {string[]}
 */
export const getSessionCacheKeys = (snapshot) => {
  if (snapshot?.isCompose) {
    return getComposeSessionCacheKeys(snapshot);
  }
  return getReadSessionCacheKeys(snapshot);
};

/**
 * @param {string} contextKey
 * @returns {object | undefined}
 */
export const getCachedChatSession = (contextKey) => {
  if (!contextKey) return undefined;
  const store = readStore();
  return store[contextKey];
};

/**
 * @param {object | null | undefined} draftContext
 * @param {object | null | undefined} snapshot
 * @returns {boolean}
 */
const draftContextMatchesSnapshot = (draftContext, snapshot) => {
  if (!draftContext || !snapshot) {
    return false;
  }
  // Check each signal independently — do not early-return false on the first mismatch.
  // VSTO reply drafts often lack inReplyTo; conversationId / RE: subject still identify them.
  if (
    draftContext.internetConversationId &&
    snapshot.inReplyTo &&
    draftContext.internetConversationId === snapshot.inReplyTo
  ) {
    return true;
  }
  if (
    draftContext.officeConversationId &&
    snapshot.conversationId &&
    draftContext.officeConversationId === snapshot.conversationId
  ) {
    return true;
  }
  if (draftContext.subject && snapshot.subject) {
    const subject = String(snapshot.subject).trim();
    const original = String(draftContext.subject).trim();
    if (
      subject === `RE: ${original}` ||
      subject === `FW: ${original}` ||
      subject === `Re: ${original}` ||
      subject === `Fw: ${original}` ||
      new RegExp(`^(RE|FW):\\s*${original.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i").test(subject)
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Reading-pane draft writes draftContext immediately before openReply(All)Form.
 * VSTO compose snapshots often lack inReplyTo/subject on first load — still consume
 * that one-shot handoff unless this is clearly a blank new email on another thread.
 * @param {object | null | undefined} draftContext
 * @param {object | null | undefined} snapshot
 * @returns {boolean}
 */
const shouldConsumeDraftHandoff = (draftContext, snapshot) => {
  if (!snapshot?.isCompose || !draftContext) {
    return false;
  }
  if (draftContextMatchesSnapshot(draftContext, snapshot)) {
    return true;
  }
  if (snapshot.inReplyTo || /^(RE|FW):/i.test(snapshot.subject || "")) {
    return true;
  }
  // Proven blank/unrelated new compose: different conversation and no reply markers.
  if (
    draftContext.officeConversationId &&
    snapshot.conversationId &&
    draftContext.officeConversationId !== snapshot.conversationId
  ) {
    return false;
  }
  // Pending handoff with incomplete compose identity (typical VSTO reply draft).
  return !!(draftContext.chatSession?.messages?.length || draftContext.sessionKey);
};

/**
 * @param {object | null | undefined} snapshot
 * @returns {object | undefined}
 */
export const resolveChatSession = (snapshot) => {
  if (!snapshot?.isCompose) {
    return getCachedChatSessionForItem(snapshot);
  }

  let draftContext = null;
  try {
    const raw = localStorage.getItem(DRAFT_CONTEXT_KEY);
    if (raw) {
      draftContext = JSON.parse(raw);
    }
  } catch {
    // ignore
  }

  const consumeHandoff = shouldConsumeDraftHandoff(draftContext, snapshot);

  if (consumeHandoff && draftContext?.chatSession?.messages?.length > 0) {
    return draftContext.chatSession;
  }

  if (consumeHandoff && draftContext) {
    const sessionKeys = [
      draftContext.sessionKey,
      draftContext.internetConversationId,
      draftContext.officeConversationId,
    ].filter(Boolean);

    for (const key of sessionKeys) {
      const session = getCachedChatSession(key);
      if (session?.messages?.length > 0) {
        return session;
      }
    }
  }

  // Same compose inspector reload only — do not match conversationId or other
  // thread-level keys, which would leak history across unrelated compose windows.
  if (snapshot.entryId) {
    const session = getCachedChatSession(snapshot.entryId);
    if (session?.messages?.length > 0) {
      return session;
    }
  }

  return undefined;
};

/**
 * @param {object | null | undefined} snapshot
 * @returns {boolean}
 */
export const hasDraftContextForItem = (snapshot) => {
  let draftContext = null;
  try {
    const raw = localStorage.getItem(DRAFT_CONTEXT_KEY);
    if (raw) {
      draftContext = JSON.parse(raw);
    }
  } catch {
    return false;
  }
  return shouldConsumeDraftHandoff(draftContext, snapshot);
};

/**
 * @param {object | null | undefined} snapshot
 */
export const clearDraftContextForItem = (snapshot) => {
  if (!hasDraftContextForItem(snapshot)) {
    return;
  }
  try {
    localStorage.removeItem(DRAFT_CONTEXT_KEY);
  } catch {
    // ignore
  }
};

/**
 * @param {object | null | undefined} snapshot
 * @returns {object | undefined}
 */
export const getCachedChatSessionForItem = (snapshot) => {
  for (const key of getSessionCacheKeys(snapshot)) {
    const session = getCachedChatSession(key);
    if (session?.messages?.length > 0) {
      return session;
    }
  }
  return undefined;
};

/**
 * @param {string} contextKey
 * @param {object} session
 */
export const setCachedChatSession = (contextKey, session) => {
  if (!contextKey || !session) return;
  const store = readStore();
  store[contextKey] = {
    ...session,
    messages: serializeMessagesForCache(session.messages || []),
    updatedAt: Date.now(),
  };
  writeStore(store);
};

/**
 * @param {object | null | undefined} snapshot
 * @param {object} session
 */
export const setCachedChatSessionForItem = (snapshot, session) => {
  getSessionCacheKeys(snapshot).forEach((key) => setCachedChatSession(key, session));
};

/**
 * @param {string} contextKey
 */
export const clearCachedChatSession = (contextKey) => {
  if (!contextKey) return;
  const store = readStore();
  if (!(contextKey in store)) return;
  delete store[contextKey];
  writeStore(store);
};

/**
 * @param {object | null | undefined} snapshot
 */
export const clearCachedChatSessionForItem = (snapshot) => {
  getSessionCacheKeys(snapshot).forEach((key) => clearCachedChatSession(key));
};
